import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { ROUTES_CONFIG } from "../routing/routes-config";
import type { RoutesConfig } from "../routing/types";

/**
 * Recursively searches for a label in the route configuration by matching path segments.
 * Traverses the route hierarchy, keeping track of the last label found to handle routes
 * without labels (like dynamic parameter routes).
 *
 * @param segments - Array of path segments to match (e.g., ['messaging', 'inbox', 'email', 'thread-123'])
 * @param config - The routes configuration object to search
 * @param segmentIndex - Current position in the segments array
 * @param lastLabel - The last label found in the route hierarchy (used for fallback)
 * @returns The most specific label found, or undefined if no label exists
 */
const findLabelInRoutes = (
  segments: string[],
  config: RoutesConfig,
  segmentIndex: number = 0,
  lastLabel?: string
): string | undefined => {
  // Base case: no more segments to process
  if (segmentIndex >= segments.length) {
    return lastLabel;
  }

  const currentSegment = segments[segmentIndex];
  const exactPath = `/${currentSegment}`;

  // Try exact match first
  if (config[exactPath]) {
    const route = config[exactPath];
    const newLabel = route.label?.() || lastLabel;

    // If this route has subRoutes, continue deeper
    if (route.subRoutes) {
      return findLabelInRoutes(segments, route.subRoutes, segmentIndex + 1, newLabel);
    }

    // No subRoutes, return the label we have
    return newLabel;
  }

  // Try to match a dynamic parameter route (e.g., /:threadId, /:enduserId)
  const dynamicRoutes = Object.keys(config).filter((key) => key.startsWith("/:"));
  for (const dynamicPath of dynamicRoutes) {
    const route = config[dynamicPath];
    if (!route) continue;
    const newLabel = route.label?.() || lastLabel;

    // If this route has subRoutes, continue deeper
    if (route.subRoutes) {
      const result = findLabelInRoutes(segments, route.subRoutes, segmentIndex + 1, newLabel);
      if (result) {
        return result;
      }
    }

    // No subRoutes, return the label we have
    return newLabel;
  }

  // No match found, return the last label we had
  return lastLabel;
};

/**
 * Hook to get the most specific page label based on the current URL path.
 * Traverses the route configuration hierarchy to find the deepest matching route with a label.
 *
 * For routes without labels (like dynamic parameter routes), it falls back to the nearest
 * parent route's label.
 *
 * @param fallback (optional) - fallback title to display if no match
 *
 * @returns The page label string, or undefined if no label is found
 *
 * @example
 * // For path /messaging/inbox/email/thread-123
 * const label = usePageLabel(); // Returns "Inbox"
 *
 * @example
 * // For path /contacts/user-456
 * const label = usePageLabel(); // Returns "Contacts"
 *
 * @example
 * // For path /dashboard
 * const label = usePageLabel(); // Returns "Dashboard"
 */
export const usePageLabel = (fallback?: string): string | undefined => {
  const location = useLocation();

  const label = useMemo(() => {
    const pathname = location.pathname;

    // Handle root path
    if (pathname === "/" || pathname === "") {
      return undefined;
    }

    // Parse pathname into segments, removing empty strings
    const segments = pathname.split("/").filter((segment) => segment !== "");

    if (segments.length === 0) {
      return undefined;
    }

    // Search for label in route configuration
    return findLabelInRoutes(segments, ROUTES_CONFIG, 0);
  }, [location.pathname]);

  return label ?? fallback;
};
